import React, { useEffect, useRef, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { renderCanvasPreview } from '../utils/canvasPreview';
import { useUserPlan } from '../hooks/useUserPlan';
import { drawWatermarkBadge } from '../utils/watermarkImage';
import { LoadingIndicator } from './LoadingIndicator';

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  fileName: string;
  editState?: {
    brightness: number;
    contrast: number;
    saturation: number;
    hue: number;
    rotation: number;
    backgroundColor?: string | 'gradient';
    shadowType: 'none' | 'medium' | 'hard' | 'floating' | 'reflection';
    centerProduct?: boolean;
    autoCrop: boolean;
    sharpness?: number;
    paddingAmount: number;
  };
  currentIndex?: number;
  totalImages?: number;
  onPrevious?: () => void;
  onNext?: () => void;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  fileName,
  editState,
  currentIndex,
  totalImages,
  onPrevious,
  onNext,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { plan } = useUserPlan();
  const [isLoading, setIsLoading] = useState(true);

  const showNavigation = totalImages !== undefined && totalImages > 1;
  const hasPrevious = currentIndex !== undefined && currentIndex > 0;
  const hasNext = currentIndex !== undefined && totalImages !== undefined && currentIndex < totalImages - 1;

  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;

    let cancelled = false;
    setIsLoading(true);

    const renderPreview = async () => {
      if (!canvasRef.current) return;

      if (!editState) {
        const img = new Image();
        await new Promise<void>((resolve, reject) => {
          img.onload = () => resolve();
          img.onerror = reject;
          img.src = imageUrl;
        });

        if (cancelled || !canvasRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        if (!cancelled) setIsLoading(false);
        return;
      }

      try {
        await renderCanvasPreview(
          canvasRef.current,
          imageUrl,
          {
            brightness: editState.brightness,
            contrast: editState.contrast,
            saturation: editState.saturation,
            hue: editState.hue,
            rotation: editState.rotation,
            backgroundColor: editState.backgroundColor,
            shadowType: editState.shadowType,
            centerProduct: editState.centerProduct,
            autoCrop: editState.autoCrop,
            sharpness: editState.sharpness,
            paddingAmount: editState.paddingAmount
          }
        );

        if (plan && !plan.has_made_purchase && canvasRef.current) {
          const canvas = canvasRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            drawWatermarkBadge(ctx, canvas.width, canvas.height);
          }
        }

        if (!cancelled) setIsLoading(false);
      } catch (error) {
        console.error('Failed to render preview:', error);
        if (!cancelled) setIsLoading(false);
      }
    };

    renderPreview();

    return () => {
      cancelled = true;
    };
  }, [isOpen, imageUrl, editState, plan]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && onPrevious && hasPrevious) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === 'ArrowRight' && onNext && hasNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onPrevious, onNext, onClose, hasPrevious, hasNext]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-[700px] w-full relative">
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 p-1.5 bg-white text-gray-500 hover:text-gray-700 rounded-full shadow-lg"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800 truncate flex-1">{fileName}</h3>
          {showNavigation && (
            <div className="text-sm text-gray-500 ml-4">
              {(currentIndex ?? 0) + 1} / {totalImages}
            </div>
          )}
        </div>
        
        <div className="relative">
          <div
            className="aspect-square rounded-lg overflow-hidden flex items-center justify-center bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+CjxyZWN0IHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMTAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgZmlsbD0iI2YxZjFmMSI+PC9yZWN0Pgo8cmVjdCB4PSIxMCIgeT0iMCIgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjZTVlNWU1Ij48L3JlY3Q+CjxyZWN0IHg9IjAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiNlNWU1ZTUiPjwvcmVjdD4KPC9zdmc+)]"
          >
            <canvas
              ref={canvasRef}
              className="max-w-full max-h-full"
              onContextMenu={(e) => e.preventDefault()}
            />
            {isLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                <LoadingIndicator />
              </div>
            )}
          </div>

          {showNavigation && (
            <>
              {hasPrevious && (
                <button
                  onClick={onPrevious}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                  title="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
              )}
              {hasNext && (
                <button
                  onClick={onNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
                  title="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              )}
            </>
          )}
        </div>

        {plan && !plan.has_made_purchase && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <Lock className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Watermarked preview &mdash; <Link to="/pricing" className="font-medium underline hover:text-amber-900">upgrade to remove</Link></span>
          </div>
        )}
      </div>
    </div>
  );
};